import React, { useEffect, useState, useRef } from 'react';
import { Box, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Tooltip } from '@mui/material';
import CommentIcon from '@mui/icons-material/Comment';
import { clampLineItemRemarks, LINE_ITEM_REMARKS_MAX_LENGTH } from '../../Utility/lineItemRemarksUtils';

export default function LineItemRemarksButton({
    enabled,
    value,
    readOnly,
    disabled,
    onSave,
    title,
    warning,
    dialogTitle,
    fieldLabel,
    required,
    secondField,
    open: controlledOpen,
    onClose: controlledOnClose,
    hideIcon,
}) {
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = typeof controlledOpen === 'boolean';
    const open = isControlled ? controlledOpen : internalOpen;
    const setOpen = (v) => {
        if (isControlled) {
            if (!v && controlledOnClose) controlledOnClose();
        } else {
            setInternalOpen(v);
        }
    };
    const [text, setText] = useState(value || '');
    const [secondText, setSecondText] = useState(secondField?.value || '');
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) {
            setText(clampLineItemRemarks(value || ''));
            if (secondField) {
                setSecondText(clampLineItemRemarks(secondField.value || ''));
            }
        }
    }, [open, value, secondField?.value]);

    const focusCaretAtEnd = () => {
        const el = inputRef.current;
        if (!el) return;
        const len = (el.value ?? '').length;
        try {
            el.focus();
            if (typeof el.setSelectionRange === 'function') {
                el.setSelectionRange(len, len);
            }
        } catch {
            /* ignore selection errors on unsupported inputs */
        }
    };

    if (!enabled) return null;

    const secondRequired = !!(secondField && secondField.required);
    const secondReadOnly = !!(secondField && (readOnly || secondField.readOnly || !secondField.required));
    const secondNoConcession = !!(secondField && !secondField.required && !readOnly);
    const saveDisabled = (required && !text.trim()) || (secondRequired && !secondText.trim());

    return (
        <>
            {hideIcon ? null : (
                <Tooltip title={title || (readOnly ? 'View remarks' : 'Line item remarks')}>
                    <span>
                        <IconButton size="small" onClick={() => setOpen(true)} disabled={disabled} sx={{ p: 0.25, mr: 0.45 }}>
                            <CommentIcon fontSize="small" color={warning ? 'warning' : ((value || secondField?.value) ? 'primary' : 'action')} />
                        </IconButton>
                    </span>
                </Tooltip>
            )}
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="sm"
                fullWidth
                TransitionProps={{
                    onEntered: () => {
                        requestAnimationFrame(() => {
                            focusCaretAtEnd();
                            requestAnimationFrame(focusCaretAtEnd);
                        });
                    },
                }}
            >
                <DialogTitle>{dialogTitle || (readOnly ? 'Line item remarks' : 'Edit remarks')}</DialogTitle>
                <DialogContent>
                    <TextField
                        inputRef={inputRef}
                        margin="dense"
                        label={readOnly ? '' : ((fieldLabel || 'Remarks') + (required ? ' *' : ''))}
                        placeholder={readOnly ? '' : 'Enter remarks for this line item'}
                        fullWidth
                        multiline
                        minRows={readOnly ? 4 : 3}
                        value={text}
                        onChange={(e) => setText(clampLineItemRemarks(e.target.value))}
                        inputProps={{ maxLength: LINE_ITEM_REMARKS_MAX_LENGTH }}
                        InputProps={{ readOnly: !!readOnly }}
                        variant="outlined"
                        sx={{ mt: 1 }}
                        helperText={readOnly ? undefined : `${text.length}/${LINE_ITEM_REMARKS_MAX_LENGTH}`}
                        FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
                    />
                    {secondField ? (
                        <Box mt={1.5}>
                            <TextField
                                margin="dense"
                                label={(secondField.label || 'Remarks') + (secondRequired ? ' *' : '')}
                                placeholder={secondReadOnly ? '' : (secondField.placeholder || '')}
                                fullWidth
                                multiline
                                minRows={3}
                                value={secondNoConcession ? '' : secondText}
                                onChange={(e) => setSecondText(clampLineItemRemarks(e.target.value))}
                                inputProps={{ maxLength: LINE_ITEM_REMARKS_MAX_LENGTH }}
                                InputProps={{ readOnly: secondReadOnly }}
                                disabled={secondReadOnly}
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                helperText={secondReadOnly ? undefined : `${secondText.length}/${LINE_ITEM_REMARKS_MAX_LENGTH}`}
                                FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0 } }}
                            />
                        </Box>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    {readOnly ? (
                        <Button onClick={() => setOpen(false)}>Close</Button>
                    ) : (
                        <>
                            <Button onClick={() => setOpen(false)}>Cancel</Button>
                            <Button
                                variant="contained"
                                disabled={saveDisabled}
                                onClick={() => {
                                    const primary = clampLineItemRemarks(text);
                                    if (secondField) {
                                        const second = clampLineItemRemarks(secondText);
                                        if (onSave) onSave(primary, second);
                                    } else {
                                        if (onSave) onSave(primary);
                                    }
                                    setOpen(false);
                                }}
                            >
                                Save
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
}
